import { TriangleAlertIcon } from 'lucide-react';
import * as React from 'react';

import { DimeAlert, DimeAlertAction, DimeAlertDescription, DimeAlertTitle } from './DimeAlert.js';
import { DimeButton } from './DimeButton.js';
import {
  DimeEmpty,
  DimeEmptyContent,
  DimeEmptyDescription,
  DimeEmptyHeader,
  DimeEmptyMedia,
  DimeEmptyTitle,
} from './DimeEmpty.js';

type DimeDetailPageStatesProps<TData> = Readonly<{
  data: TData | null | undefined;
  loading: boolean;
  error?: { message: string } | null;
  entityLabel: string;
  notFoundIcon?: React.ReactNode;
  notFoundAction?: React.ReactNode;
  onRetry?: () => void;
  /** The page's own loading shell -- required, so the states never guess at a layout they can't see. */
  skeleton: React.ReactNode;
  children: (data: TData) => React.ReactNode;
}>;

export function DimeDetailPageStates<TData>({
  data,
  loading,
  error,
  entityLabel,
  notFoundIcon,
  notFoundAction,
  onRetry,
  skeleton,
  children,
}: DimeDetailPageStatesProps<TData>) {
  if (loading && !data) {
    return <>{skeleton}</>;
  }

  if (!data && error) {
    return (
      <DimeAlert variant="destructive">
        <TriangleAlertIcon />
        <DimeAlertTitle>Could not load this {entityLabel.toLowerCase()}</DimeAlertTitle>
        <DimeAlertDescription>{error.message}</DimeAlertDescription>
        {onRetry ? (
          <DimeAlertAction>
            <DimeButton variant="outline" size="sm" onClick={onRetry}>
              Try again
            </DimeButton>
          </DimeAlertAction>
        ) : null}
      </DimeAlert>
    );
  }

  if (!data) {
    return (
      <DimeEmpty className="border border-dashed">
        <DimeEmptyHeader>
          {notFoundIcon ? <DimeEmptyMedia variant="icon">{notFoundIcon}</DimeEmptyMedia> : null}
          <DimeEmptyTitle>{entityLabel} not found</DimeEmptyTitle>
          <DimeEmptyDescription>
            It may have been deleted, or the link is incorrect.
          </DimeEmptyDescription>
        </DimeEmptyHeader>
        {notFoundAction ? <DimeEmptyContent>{notFoundAction}</DimeEmptyContent> : null}
      </DimeEmpty>
    );
  }

  return <>{children(data)}</>;
}
